import type { Product } from 'src/components/restructure/product/types/product'
import type { ProductItem } from 'src/components/restructure/product/types/productItem'
import type { ReviewItemList } from 'src/components/restructure/product/types/review'
import { buildAdditionalProperty } from 'src/utils/parseAdditionalProperty'

function isEan13(value: string | undefined | null): value is string {
  if (!value) {
    return false
  }

  const digits = String(value).replace(/\D/g, '')

  return digits.length === 13
}

function pickGtin13(item: ProductItem): string | undefined {
  if (isEan13(item.ean)) {
    return item.ean.replace(/\D/g, '')
  }

  if (isEan13(item.referenceId)) {
    return String(item.referenceId).replace(/\D/g, '')
  }

  return undefined
}

function uniqUrls(urls: Array<string | undefined | null>): string[] {
  const seen = new Set<string>()
  const out: string[] = []

  for (const u of urls) {
    if (!u) {
      continue
    }

    const t = u.trim()

    if (!t || seen.has(t)) {
      continue
    }

    seen.add(t)
    out.push(t)
  }

  return out
}

function collectProductGroupImages(product: Product): string[] {
  const seo = product.seo?.images?.map((i) => i.url) ?? []
  const sku =
    product.items?.flatMap((i) => i.images?.map((img) => img.imageUrl) ?? []) ??
    []

  return uniqUrls([...seo, ...sku])
}

/** Placeholder sent when a variant dimension does not apply (must not affect schema/variesBy). */
function isNotApplicableSpecValue(value: string | undefined | null): boolean {
  if (value == null || !String(value).trim()) {
    return true
  }

  return /^n\/a$/i.test(String(value).trim())
}

function firstOrJoin(
  arr: string[] | null | undefined,
  joiner = ', '
): string | undefined {
  if (!arr?.length) {
    return undefined
  }

  const parts = arr
    .map((validSpecification) =>
      validSpecification == null ? '' : String(validSpecification).trim()
    )
    .filter(
      (validSpecification) =>
        validSpecification.length > 0 &&
        !isNotApplicableSpecValue(validSpecification)
    )

  if (!parts.length) {
    return undefined
  }

  return parts.length === 1 ? parts[0] : parts.join(joiner)
}

function buildVariantName(
  item: ProductItem,
  productName: string,
  attributes?: {
    color?: string
    size?: string
    flavor?: string
    voltage?: string
  }
): string {
  if (item.name?.trim()) {
    return item.name.trim()
  }

  const parts = [
    productName,
    attributes?.color,
    attributes?.size,
    attributes?.flavor,
    attributes?.voltage,
  ].filter(Boolean)

  return parts.join(' ').trim()
}

function itemConditionSchema(conditions?: string): string {
  return conditions?.toLowerCase() === 'novo'
    ? 'https://schema.org/NewCondition'
    : 'https://schema.org/UsedCondition'
}

/** Schema.org PropertyValue for Product.additionalProperty */
function schemaPropertyValue(
  name: string,
  value: string
): Record<string, unknown> {
  return {
    '@type': 'PropertyValue',
    name,
    value,
  }
}

function buildVideoSubject(
  videoData: Product['seo']['videoData'] | null | undefined
): Record<string, unknown> | undefined {
  if (!videoData?.name && !videoData?.contentUrl) {
    return undefined
  }

  const videoObject: Record<string, unknown> = {
    '@type': 'VideoObject',
  }

  if (videoData.name) {
    videoObject.name = videoData.name
  }

  if (videoData.description) {
    videoObject.description = videoData.description
  }

  if (videoData.thumbnailUrl) {
    videoObject.thumbnailUrl = videoData.thumbnailUrl
  }

  if (videoData.uploadDate) {
    videoObject.uploadDate = videoData.uploadDate
  }

  if (videoData.contentUrl) {
    videoObject.contentUrl = videoData.contentUrl
  }

  return Object.keys(videoObject).length > 1 ? videoObject : undefined
}

function mapReviewsJsonLd(
  reviews: ReviewItemList[] | undefined
): Array<Record<string, unknown>> | undefined {
  // TODO: Check problem with showing less reviews than reviewCount
  if (!reviews?.length) {
    return undefined
  }

  return reviews.map((review) => ({
    '@type': 'Review',
    reviewBody: review.body,
    author: {
      '@type': 'Person',
      name: review.firstname,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: String(review.note),
      bestRating: '5',
      worstRating: '0',
    },
  }))
}

function mapAggregateRatingJsonLd(
  product: Product
): Record<string, unknown> | undefined {
  if (!product.review) {
    return undefined
  }

  return {
    '@type': 'AggregateRating',
    ratingValue: String(product.review.totalRatingsAverageNote ?? 0),
    reviewCount: product.review.totalReviews ?? 0,
    bestRating: '5',
    worstRating: '0',
  }
}

type BuildArgs = {
  product: Product
  siteUrl: string
  slug: string
}

export function buildProductGroupJsonLdArray({
  product,
  siteUrl,
  slug,
}: BuildArgs): Array<Record<string, unknown>> {
  const origin = siteUrl.replace(/\/+$/, '')
  const productPath = `/${slug.replace(/^\/+/, '')}/p`
  const productUrl = `${origin}${productPath}`

  const groupImages = collectProductGroupImages(product)
  const aggregateRating = mapAggregateRatingJsonLd(product)
  const review = mapReviewsJsonLd(product.review?.reviews ?? undefined)
  const video = buildVideoSubject(product.seo?.videoData)

  const additionalPropertySourceItem =
    product.items?.find(
      (item) =>
        (item.productBenefits?.trim().length ?? 0) > 0 ||
        (item.technicalInformation?.trim().length ?? 0) > 0
    ) ?? product.items?.[0]

  const additionalProperty = additionalPropertySourceItem
    ? buildAdditionalProperty(
        additionalPropertySourceItem.productBenefits ?? '',
        additionalPropertySourceItem.technicalInformation ?? ''
      )
    : []

  const productVariants =
    product.items?.map((item) => {
      const color = firstOrJoin(item.Cor ?? undefined)
      const size = firstOrJoin(item.Tamanho ?? undefined)
      const flavor = firstOrJoin(item.Sabor ?? undefined)
      const voltage = firstOrJoin(item.Voltagem ?? undefined)
      const { linkText } = item
      const variantName = buildVariantName(item, product.productName, {
        color,
        size,
        flavor,
        voltage,
      })

      const skuImage = item.images?.[0]?.imageUrl
      const image = skuImage ?? groupImages[0]

      const { sellerOffer } = item.sellerDefault
      const { isAvailable, listPrice, cashPrice, price, priceValidUntil } =
        sellerOffer

      const listPriceValue = Number(listPrice)
      const salePrice = Number(cashPrice ?? price)

      const offer: Record<string, unknown> = {
        '@type': 'Offer',
        url: `${origin}/${linkText}/p?skuId=${item.itemId}`,
        priceCurrency: 'BRL',
        availability: isAvailable
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
        itemCondition: itemConditionSchema(product.conditions),
      }

      offer.price = salePrice
      offer.priceSpecification = [
        {
          '@type': 'UnitPriceSpecification',
          priceType: 'https://schema.org/ListPrice',
          price: listPriceValue,
          priceCurrency: 'BRL',
        },
        {
          '@type': 'UnitPriceSpecification',
          priceType: 'https://schema.org/SalePrice',
          price: salePrice,
          priceCurrency: 'BRL',
        },
      ]

      if (priceValidUntil) {
        offer.priceValidUntil = priceValidUntil
      }

      const gtin13 = pickGtin13(item)

      const variant: Record<string, unknown> = {
        '@type': 'Product',
        sku: item.itemId,
        name: variantName,
        offers: offer,
      }

      const additionalPropertyValues: Array<Record<string, unknown>> = []

      if (gtin13) {
        variant.gtin13 = gtin13
      }

      if (color) {
        variant.color = color
      }

      if (size) {
        variant.size = size
      }

      if (flavor) {
        additionalPropertyValues.push(schemaPropertyValue('flavor', flavor))
      }

      if (voltage) {
        additionalPropertyValues.push(schemaPropertyValue('voltage', voltage))
      }

      if (additionalPropertyValues.length > 0) {
        variant.additionalProperty = additionalPropertyValues
      }

      if (image) {
        variant.image = image
      }

      return variant
    }) ?? []

  const hasColor = productVariants.some((variant) => variant.color)
  const hasSize = productVariants.some((variant) => variant.size)
  const hasFlavor =
    productVariants.some((variant) =>
      (variant.additionalProperty as Array<{ name: string }> | undefined)?.some(
        (prop) => prop.name === 'flavor'
      )
    ) ?? false

  const hasVoltage =
    productVariants.some((variant) =>
      (variant.additionalProperty as Array<{ name: string }> | undefined)?.some(
        (prop) => prop.name === 'voltage'
      )
    ) ?? false

  const variesBy = [
    hasColor ? 'https://schema.org/color' : null,
    hasSize ? 'https://schema.org/size' : null,
    hasFlavor ? 'flavor' : null,
    hasVoltage ? 'voltage' : null,
  ].filter(Boolean) as string[]

  const productGroup: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'ProductGroup',
    productGroupID: product.productId,
    name: product.productName,
    description: product.description,
    url: productUrl,
    brand: {
      '@type': 'Brand',
      name: product.brand,
    },
    image: groupImages.length ? groupImages : undefined,
    additionalProperty:
      additionalProperty.length > 0 ? additionalProperty : undefined,
    variesBy: variesBy.length ? variesBy : undefined,
    hasVariant: productVariants.length ? productVariants : undefined,
  }

  if (aggregateRating) {
    productGroup.aggregateRating = aggregateRating
  }

  if (review?.length) {
    productGroup.review = review
  }

  if (video) {
    productGroup.subjectOf = video
  }

  // Prune undefined values (invalid in JSON-LD)
  const prune = (obj: unknown): unknown => {
    if (obj === null || obj === undefined) {
      return undefined
    }

    if (Array.isArray(obj)) {
      const mapped = obj.map(prune).filter((v) => v !== undefined && v !== null)

      return mapped
    }

    if (typeof obj === 'object') {
      const entries = Object.entries(obj as Record<string, unknown>)
      const out: Record<string, unknown> = {}

      for (const [k, v] of entries) {
        const p = prune(v)

        if (
          p !== undefined &&
          p !== null &&
          !(Array.isArray(p) && p.length === 0)
        ) {
          out[k] = p
        }
      }

      return out
    }

    return obj
  }

  const cleanedGroup = prune(productGroup) as Record<string, unknown>

  return [cleanedGroup]
}
